import { read_cookie } from 'sfcookies';
import { apiUrl, hostUrl, UseUrlManagerCleanUrl } from '../../../utils/url-manager';
import { EmailMessage, SalesConversation } from '../../../../types/entities';

/**
 * The conversation panel talks to HQ-API directly rather than through a react-admin data provider.
 *
 * Two reasons. The panel needs one conversation, its messages and three write actions, which is not
 * a resource shape, and building it as one would mean a fourth copy of the 400 line provider every
 * other resource duplicates. More importantly, **all 55 of those providers swallow a non-2xx and
 * hand react-admin an empty result** (HQ-06), so a reply that MailGun refused would render as a
 * success with an empty thread. On this surface that is the worst possible failure, so every call
 * below reports what actually happened.
 */

/** Which request the panel is attached to. Exactly one, matching the API's own rule. */
export type ConversationSubject =
    | { contactForm: number }
    | { enterpriseRequest: number };

export type ActionResult = {
    ok: boolean,
    message: string
};

const jsonHeaders = (): Headers => new Headers({
    'Content-Type': 'application/json',
    'token': read_cookie('auth'),
    'hq': 'hq',
});

/**
 * The query fragment naming the subject, for reads.
 */
const subjectFilter = (subject: ConversationSubject): string =>
    'contactForm' in subject
        ? `contactForm.id=${ subject.contactForm }`
        : `enterpriseRequest.id=${ subject.enterpriseRequest }`;

/**
 * Reads the message out of an HQ-API answer.
 *
 * The actions answer `{status, message}` on both outcomes, so the message is worth showing rather
 * than replacing with something generic. A response that is not JSON at all still has to produce a
 * sentence.
 */
const readMessage = async (response: Response, fallback: string): Promise<string> => {
    try {
        const body: any = await response.json();
        if (typeof body?.message === 'string' && body.message.length > 0) {
            return body.message;
        }
    } catch {
        // Not JSON. The status is all there is to go on.
    }

    return fallback;
};

/**
 * The conversation for this request, or null when nobody has started one yet.
 *
 * Null is the ordinary state: a thread is created by the first reply or note, so most records have
 * none. It is not an error and the panel renders normally without one.
 */
export const loadConversation = async (subject: ConversationSubject): Promise<SalesConversation|null> => {
    const url: string = UseUrlManagerCleanUrl({
        url: `${ apiUrl }sales_conversations?${ subjectFilter(subject) }`,
    });
    const response: Response = await fetch(new Request(url, { method: 'GET', headers: jsonHeaders() }));
    if (!response.ok) {
        throw new Error(await readMessage(response, 'Could not load the conversation.'));
    }

    const body: any = await response.json();
    const members: SalesConversation[] = body?.['hydra:member'] ?? body?.member ?? [];

    return members.length > 0 ? members[0] : null;
};

/**
 * The thread itself, oldest first, which is the order a conversation reads in.
 */
export const loadMessages = async (conversationId: number): Promise<EmailMessage[]> => {
    const url: string = UseUrlManagerCleanUrl({
        url: `${ apiUrl }email_messages?salesConversation.id=${ conversationId }&sortOrder[createdAt]=asc&itemsPerPage=200`,
    });
    const response: Response = await fetch(new Request(url, { method: 'GET', headers: jsonHeaders() }));
    if (!response.ok) {
        throw new Error(await readMessage(response, 'Could not load the messages.'));
    }

    const body: any = await response.json();

    return body?.['hydra:member'] ?? body?.member ?? [];
};

/**
 * Conversations where they have written and we have not answered.
 *
 * `status=open` is the ball being with us, and `exists[lastMessageAt]` is what separates a live
 * exchange from a request nobody has started yet — the second kind is already covered by the two
 * "new" cards on the dashboard, so listing them again would be noise.
 *
 * @param limit how many rows the card shows
 */
export const loadAwaitingReply = async (limit: number = 10): Promise<SalesConversation[]> => {
    const url: string = UseUrlManagerCleanUrl({
        url: `${ apiUrl }sales_conversations?status=open&exists[lastMessageAt]=true`
            + `&sortOrder[lastMessageAt]=desc&itemsPerPage=${ limit }`,
    });
    const response: Response = await fetch(new Request(url, { method: 'GET', headers: jsonHeaders() }));
    if (!response.ok) {
        throw new Error(await readMessage(response, 'Could not load the conversations.'));
    }

    const body: any = await response.json();

    return body?.['hydra:member'] ?? body?.member ?? [];
};

/** Somebody a conversation can be handed to. */
export type Assignee = {
    id: number,
    name: string|null,
    role: string|null
};

/**
 * Who the signed-in admin may hand a conversation to, by name.
 *
 * HQ-API decides this rather than HQ: admins holding `HQ_Sales::conversation_reassign_any` reach
 * everybody who can work conversations, everybody else only their own role, and the handover route
 * applies the same rule. It replaced reading `admin_users`, which a sales role cannot list.
 */
export const loadAssignees = async (): Promise<Assignee[]> => {
    const url: string = UseUrlManagerCleanUrl({ url: `${ hostUrl }hq/sales/conversation/assignees` });
    const response: Response = await fetch(new Request(url, { method: 'GET', headers: jsonHeaders() }));
    if (!response.ok) {
        throw new Error(await readMessage(response, 'Could not load who this can be handed to.'));
    }

    const body: any = await response.json();

    return Array.isArray(body?.assignees) ? body.assignees : [];
};

/**
 * @param path the action route, e.g. `sales/conversation/reply`
 * @param payload the body, which always names its subject
 * @param fallback what to say when the API gave no message of its own
 */
const post = async (path: string, payload: object, fallback: string): Promise<ActionResult> => {
    const url: string = UseUrlManagerCleanUrl({ url: `${ hostUrl }hq/${ path }` });
    let response: Response;
    try {
        response = await fetch(new Request(url, {
            method: 'POST',
            headers: jsonHeaders(),
            body: JSON.stringify(payload),
        }));
    } catch {
        // The request never reached HQ-API, so nothing is known about whether it happened.
        return { ok: false, message: 'Could not reach the server. Nothing was sent.' };
    }

    return { ok: response.ok, message: await readMessage(response, fallback) };
};

/**
 * Clears the unread replies on this conversation, called when the panel opens one.
 *
 * Fire and forget: the badge being a minute stale is nobody's problem, and a failure here must not
 * stop the thread rendering. Creates nothing — a request merely looked at does not get a thread.
 */
export const markRead = (subject: ConversationSubject): Promise<ActionResult> =>
    post('sales/conversation/read', { ...subject }, 'Could not mark the replies as read.');

export const sendReply = (
    subject: ConversationSubject,
    replySubject: string,
    body: string
): Promise<ActionResult> =>
    post('sales/conversation/reply', { ...subject, subject: replySubject, body }, 'The reply was not sent.');

export const addNote = (subject: ConversationSubject, note: string): Promise<ActionResult> =>
    post('sales/conversation/note', { ...subject, note }, 'The note was not saved.');

export const reassign = (
    subject: ConversationSubject,
    assignTo: number,
    reason: string
): Promise<ActionResult> =>
    post('sales/conversation/reassign', { ...subject, assignTo, reason }, 'The conversation was not handed over.');
